import InitialState from '@/InitialState';
import Log from '@/Utils/Log';
import { userPrivacySetting } from '@/Models';
import UsersService from '@/Services/UsersService';
import { apiClient } from '@/Clients/ApiClient';

const consentManagerTimeoutMs = 10000;
const consentManagerPollIntervalMs = 100;

const consent = InitialState.analyticsConfig?.consent;
const user = InitialState.userContext?.user;
const serverPrivacy = user?.privacySettingFlags
    ? { flags: user.privacySettingFlags, updatedAt: user.privacySettingsUpdatedAt }
    : null;

function purposesToFlags(purposes) {
    let flags = userPrivacySetting.essential;
    if (purposes.Functional) flags |= userPrivacySetting.functional;
    if (purposes.Analytics) flags |= userPrivacySetting.analytic;
    if (purposes.Advertising) flags |= userPrivacySetting.marketing;
    return flags;
}

function flagsToPurposes(flags) {
    return {
        Functional: (flags & userPrivacySetting.functional) !== 0,
        Analytics: (flags & userPrivacySetting.analytic) !== 0,
        Advertising: (flags & userPrivacySetting.marketing) !== 0,
    };
}

async function setConsentCookie(flags, updatedAt) {
    await new UsersService(apiClient).setAnalyticsCookie({
        privacySettingFlags: flags,
        privacySettingsUpdatedAt: updatedAt,
    });
}

function getNativePurposes() {
    if (serverPrivacy) {
        return flagsToPurposes(serverPrivacy.flags);
    }

    const raw = consent?.purposes ?? { Advertising: false, Analytics: false, Functional: true };
    return Object.fromEntries(
        Object.entries(raw).map(([key, value]) => [key.charAt(0).toUpperCase() + key.slice(1), value]),
    );
}

const nativeProvider = {
    whenReady: () => Promise.resolve(null),
    getAdvertisingConsent: () => getNativePurposes().Advertising,
    onConsentChange: (callback) => {
        callback({ purposes: getNativePurposes() });
        return () => {};
    },
    showConsentManager: () => {},
    whenPurposeConsented: (purpose) => {
        if (getNativePurposes()[purpose]) {
            return Promise.resolve();
        }
        return new Promise(() => {});
    },
    hasConsentUI: false,
};

const transcendProvider = (() => {
    let readyPromise = null;

    function isAirgapMethodAvailable(method) {
        return typeof window.airgap?.[method] === 'function';
    }

    function whenReady() {
        if (readyPromise) {
            return readyPromise;
        }

        readyPromise = new Promise((resolve) => {
            if (isAirgapMethodAvailable('ready')) {
                window.airgap.ready((airgap) => resolve(airgap));
                return;
            }

            const pollInterval = setInterval(() => {
                if (isAirgapMethodAvailable('ready')) {
                    clearInterval(pollInterval);
                    clearTimeout(timeout);
                    window.airgap.ready((airgap) => resolve(airgap));
                }
            }, consentManagerPollIntervalMs);

            const timeout = setTimeout(() => {
                clearInterval(pollInterval);
                Log.warn('[Privacy] Consent manager script failed to load within timeout');
                resolve(null);
            }, consentManagerTimeoutMs);
        });

        return readyPromise;
    }

    let isSyncingFromServer = false;

    function syncServerToTranscend(consentManager) {
        if (!serverPrivacy) {
            return;
        }

        let transcendPurposes = null;
        try {
            transcendPurposes = consentManager.getConsent()?.purposes;
        }
        catch (error) {
            Log.warn('[Privacy] Failed to read Transcend consent for comparison', error);
        }

        const serverPurposes = flagsToPurposes(serverPrivacy.flags);

        if (transcendPurposes) {
            const alreadySynced
                = transcendPurposes.Functional === serverPurposes.Functional
                && transcendPurposes.Analytics === serverPurposes.Analytics
                && transcendPurposes.Advertising === serverPurposes.Advertising;

            if (alreadySynced) {
                return;
            }
        }

        const auth = window.__airgapAuth;
        if (!auth) {
            Log.warn('[Privacy] Cannot sync server privacy to Transcend: no airgap auth event captured');
            return;
        }

        try {
            isSyncingFromServer = true;
            consentManager.setConsent(auth, serverPurposes);
            Log.info('[Privacy] Synced server privacy settings to Transcend');
        }
        catch (error) {
            Log.warn('[Privacy] Failed to sync server privacy to Transcend', error);
        }
        finally {
            isSyncingFromServer = false;
        }
    }

    function onTranscendConsentChange(consentData) {
        if (isSyncingFromServer) {
            return;
        }

        const purposes = consentData?.purposes;
        if (!purposes) {
            return;
        }

        const flags = purposesToFlags(purposes);

        setConsentCookie(flags, new Date().toISOString());
    }

    function getAdvertisingConsent() {
        if (!isAirgapMethodAvailable('getConsent')) {
            return false;
        }

        try {
            const consent = window.airgap.getConsent();
            return consent?.purposes?.Advertising;
        }
        catch (error) {
            Log.warn('[Privacy] Failed to read airgap consent', error);
            return false;
        }
    }

    function onConsentChange(callback) {
        let cleanupFn = () => {};
        let cancelled = false;

        whenReady().then((consentManager) => {
            if (cancelled || !consentManager) {
                return;
            }

            try {
                const initialConsent = consentManager.getConsent();
                callback(initialConsent);
            }
            catch (error) {
                Log.error(error);
            }

            const listener = ({ detail: { consent } }) => {
                callback(consent);
            };

            consentManager.addEventListener('consent-change', listener);

            cleanupFn = () => {
                consentManager.removeEventListener('consent-change', listener);
            };
        });

        return () => {
            cancelled = true;
            cleanupFn();
        };
    }

    function whenPurposeConsented(purpose) {
        return whenReady().then((consentManager) => {
            if (!consentManager) {
                return;
            }

            return new Promise((resolve) => {
                const consent = consentManager.getConsent();

                if (consent?.purposes?.[purpose]) {
                    resolve();
                    return;
                }

                const listener = ({ detail: { consent: updatedConsent } }) => {
                    if (updatedConsent?.purposes?.[purpose]) {
                        consentManager.removeEventListener('consent-change', listener);
                        resolve();
                    }
                };

                consentManager.addEventListener('consent-change', listener);
            });
        });
    }

    function showConsentManager() {
        if (window.transcend) {
            window.transcend.showConsentManager();
            return;
        }

        whenReady().then(() => {
            if (window.transcend) {
                window.transcend.showConsentManager();
            }
            else {
                Log.warn('[Privacy] Consent manager UI not available');
            }
        });
    }

    function initialize() {
        whenReady().then((consentManager) => {
            if (!consentManager) {
                return;
            }
            syncServerToTranscend(consentManager);
            consentManager.addEventListener('consent-change', ({ detail: { consent: updatedConsent } }) => {
                onTranscendConsentChange(updatedConsent);
            });
        });
    }

    return {
        initialize,
        whenReady,
        getAdvertisingConsent,
        onConsentChange,
        showConsentManager,
        whenPurposeConsented,
        hasConsentUI: true,
    };
})();

const isTranscend = consent?.provider === 'transcend';
const provider = isTranscend ? transcendProvider : nativeProvider;

if (isTranscend) {
    transcendProvider.initialize();
}

/**
 * @returns {boolean} True if advertising consent is granted
 */
export function getAdvertisingConsent() {
    return provider.getAdvertisingConsent();
}

/**
 * @param {Function} callback - Callback function that receives the consent object
 * @returns {Function} Cleanup function to remove the listener
 */
export function attachConsentChangeListener(callback) {
    return provider.onConsentChange(callback);
}

export function openConsentManager() {
    provider.showConsentManager();
}

/**
 * @param {boolean} isVideoEmbed - Whether the content is a video embed (YouTube, Vimeo only)
 * @param {boolean} isAdvertisingConsentGranted - Current advertising consent status
 * @returns {boolean} True if content should be blocked
 */
export function shouldBlockContent(isVideoEmbed, isAdvertisingConsentGranted) {
    if (isVideoEmbed) {
        return false;
    }

    return !isAdvertisingConsentGranted;
}

/**
 * @param {string} purpose - Consent purpose name (e.g. 'Analytics', 'Advertising', 'Functional')
 * @returns {Promise} Resolves when the specified purpose consent is granted
 */
export function whenPurposeConsented(purpose) {
    return provider.whenPurposeConsented(purpose);
}

/**
 * @returns {boolean} True if external consent UI handles consent
 */
export function hasExternalConsentUI() {
    return provider.hasConsentUI;
}
